import getConfig from "next/config";
import _omit from "lodash/omit";
import {
  action,
  computed,
  makeObservable,
  observable,
  reaction,
  runInAction,
  /*toJS,*/
} from "mobx";
import RegionModal from "common/Location/RegionModal/RegionModal";
// import LocationModal from "common/Location/LocationModal/LocationModal";
import { IStoreElement } from "types/store";
import { ExtendedNextPageContext } from "types/app";
import {
  getDaysMaxAgeCookie,
  getServerRequestCookie,
  appendServerResponseCookie,
  setClientCookie,
  removeClientCookie,
  getClientCookie,
} from "helpers/cookies";
import { gtm } from "helpers/analytics";
import {
  BITRIX_LAST_CITY_COOKIE_NAME,
  DEFAULT_REGION_CODE,
  BITRIX_REGION_COOKIE_NAME,
  STORAGE_PREFIX,
  DEFAULT_REGION_NUMBER,
  REGION_SUPPRESSED_ROUTES,
} from "constants/variables";

export const NEED_TO_CHECK_COOKIE_NAME = `${STORAGE_PREFIX}v4_region-need-to-check`;
export const REGION_COOKIE_NAME = `${STORAGE_PREFIX}v4_region`;

const { publicRuntimeConfig } = getConfig();

type DefaultRegionData = Required<Omit<Entity.Region, "cityId">> &
  Partial<Pick<Entity.Region, "cityId">>;

const defaultRegion: DefaultRegionData = {
  id: DEFAULT_REGION_CODE,
  lat: "55.753206",
  long: "37.622509",
  name: "Московская область",
  num: "77",
  names: {
    i: "Москва и область",
    r: "Москвы и области",
    p: "Москве и области",
  },
  cityNames: {
    i: "Москва",
    r: "Москвы",
    p: "Москве",
  },
};

export default class Location implements IStoreElement {
  private ctx?: ExtendedNextPageContext;
  @observable regions = [];
  @observable _region: Entity.Region = defaultRegion;
  @observable regionByIP: Entity.Region = null;

  constructor() {
    makeObservable(this);
    /* Trouble with scope in async arrow functions, maybe will fixed with https://github.com/swc-project/swc/issues/2402 */
    reaction(() => this._region, this.regionReaction.bind(this));
  }

  toJSON() {
    return {
      /* todo: enable after refactor, if needed */
      /*regions: toJS(this.regions),*/
      _region: this._region,
      regionByIP: this.regionByIP,
    };
  }

  @action
  async regionReaction(value) {
    if (this._region?.cityId) {
      setClientCookie(BITRIX_LAST_CITY_COOKIE_NAME, this._region.cityId, {
        domain: publicRuntimeConfig.COOKIE_FORWARD_DOMAIN,
        maxAge: getDaysMaxAgeCookie(300),
        path: "/",
      });
    } else {
      removeClientCookie(BITRIX_LAST_CITY_COOKIE_NAME, {
        domain: publicRuntimeConfig.COOKIE_FORWARD_DOMAIN,
        path: "/",
      });
    }
    if (value?.id && this._region?.id !== value?.id) {
      await this.ctx.store.settings.fetch();
    }
  }

  @action
  async getRegions(): Promise<void> {
    const { data, errors } = await this.ctx.api.methods.settings.regions();
    if (data && data.regions) {
      runInAction(() => {
        this.regions = data.regions;
      });
    }
    if (errors) {
      errors.forEach(err => {
        console.error("Ошибка при получении регионов", err.message);
      });
    }
  }

  @action
  requestRegion(): void {
    gtm.locationModalRequest();
    this.ctx.store.notifications.show({
      type: "modal",
      component: RegionModal,
    });
  }

  // @action
  // requestAddress(): void {
  //   this.ctx.store.notifications.show({
  //     type: "modal",
  //     component: LocationModal,
  //   });
  // }

  setCookiesClient = (region: Entity.Region): void => {
    if (region) {
      setClientCookie(REGION_COOKIE_NAME, region, {
        domain: publicRuntimeConfig.COOKIE_FORWARD_DOMAIN,
        maxAge: getDaysMaxAgeCookie(300),
      });
      // todo: удалить после полного перехода на v2
      setClientCookie(BITRIX_REGION_COOKIE_NAME, region.id, {
        domain: publicRuntimeConfig.COOKIE_FORWARD_DOMAIN,
        maxAge: getDaysMaxAgeCookie(300),
      });
    }
  };

  setCookiesServer = (region: Entity.Region): void => {
    if (region) {
      appendServerResponseCookie(this.ctx, {
        name: REGION_COOKIE_NAME,
        value: region,
        domain: publicRuntimeConfig.COOKIE_FORWARD_DOMAIN,
        maxAge: getDaysMaxAgeCookie(300),
      });
      // todo: удалить после полного перехода на v2
      appendServerResponseCookie(this.ctx, {
        name: BITRIX_REGION_COOKIE_NAME,
        value: region.id,
        domain: publicRuntimeConfig.COOKIE_FORWARD_DOMAIN,
        maxAge: getDaysMaxAgeCookie(300),
      });
    }
  };

  set region(region: Entity.Region) {
    if (this._region !== null || this.regionByIP?.id !== region.id) {
      this.setNeedToCheckRegion(false);
    }
    this._region = region;
    this.setCookiesClient(region);
  }

  @computed
  get region(): Entity.Region {
    return this._region;
  }

  // @action
  // async updateRegionFromProfile(): Promise<void> {
  //   /* Update region from profile */
  //   const regionIDFromProfile = this.ctx.store.user.profile?.regionID;
  //   if (regionIDFromProfile && regionIDFromProfile !== this.region?.id) {
  //     this.region = await this.getRegionById(regionIDFromProfile);
  //   }
  // }

  getRegionCookieClient = (): Entity.Region | undefined => {
    return getClientCookie(REGION_COOKIE_NAME) as Entity.Region;
  };

  getIP = (): string => {
    const req = this.ctx.req;
    let ip = req
      ? req.headers["cf-connecting-ip"] ||
        String(req.headers["x-forwarded-for"] || "")
          .split(",")
          .pop() ||
        req.headers["x-real-ip"]
      : null;
    if (ip && ip[0] === ":") {
      ip = ip.slice(7);
    }
    return ip ? String(ip) : undefined;
  };

  async getRegionByIP(): Promise<Entity.Region> {
    const ip = this.getIP() || null;
    const { data, errors } = await this.ctx.api.methods.settings.detectRegion({
      ip,
    });

    if (errors) {
      errors.forEach(err => {
        console.error("Ошибка при определении региона по IP", err.message);
      });
    }
    return data?.detectRegion || null;
  }

  setNeedToCheckRegion(needToCheck: boolean): void {
    if (typeof window === "undefined") {
      appendServerResponseCookie(this.ctx, {
        name: NEED_TO_CHECK_COOKIE_NAME,
        value: needToCheck ? 1 : 0,
        domain: publicRuntimeConfig.COOKIE_FORWARD_DOMAIN,
        path: "/",
        maxAge: getDaysMaxAgeCookie(300),
      });
    } else {
      setClientCookie(NEED_TO_CHECK_COOKIE_NAME, needToCheck ? 1 : 0, {
        domain: publicRuntimeConfig.COOKIE_FORWARD_DOMAIN,
        path: "/",
        maxAge: getDaysMaxAgeCookie(300),
      });
    }
  }

  /* todo: refactor */
  // true, undefined - нужно повторно запросить регион в модалке
  // false - не нужно
  needToCheckRegion(): boolean {
    let result;
    if (typeof window === "undefined") {
      result = getServerRequestCookie(this.ctx, NEED_TO_CHECK_COOKIE_NAME);
    } else {
      result = getClientCookie(NEED_TO_CHECK_COOKIE_NAME);
    }
    return result === undefined || result === null || result === 1;
  }

  needToShowModal = (): boolean => {
    if (
      this.needToCheckRegion() &&
      this.regionByIP &&
      this._region?.id === this.regionByIP.id
    ) {
      return false;
    }

    return !this.region || this.needToCheckRegion();
  };

  // todo: используйте всегда regionID (location.regionID) для всех запросов bff
  @computed
  get regionID(): string {
    return this.region?.id
      ? String(this.region.id).toLowerCase()
      : DEFAULT_REGION_CODE;
  }

  @computed
  get regionNum(): string {
    return this.region?.num || DEFAULT_REGION_NUMBER;
  }

  async getRegionById(region: Entity.ID): Promise<Entity.Region> {
    if (region) {
      /* Find in already fetched regions */
      const stored = this.regions.find(item => item.id === region);
      if (stored) {
        return stored;
      }

      const { data, errors } = await this.ctx.api.methods.settings.region({
        regionID: region,
      });

      if (errors) {
        errors.forEach(err => {
          console.error("Ошибка при определении региона", err.message);
        });
      }
      return data?.region || null;
    }
    return null;
  }

  @action
  async initSeverRegionValue(ctx: ExtendedNextPageContext): Promise<void> {
    /* Set regions already fetched in middleware */
    if (ctx.req?.sharedData?.regions) {
      this.regions = ctx.req.sharedData.regions;
    }

    const isRouteRegionless =
      ctx.asPath.includes(".html") ||
      REGION_SUPPRESSED_ROUTES.includes(ctx.pathname);
    const regionSlugFromQuery = this.ctx.query?.region as string;
    const regionFromUrl = await this.getRegionById(regionSlugFromQuery);
    const regionFromCookie = getServerRequestCookie(
      this.ctx,
      REGION_COOKIE_NAME
    ) as Entity.Region;
    const region = regionFromUrl || this.region;

    if (regionFromCookie) {
      const regionsAreEqual =
        region &&
        String(region.id).toLowerCase() ===
          String(regionFromCookie.id).toLowerCase();

      if (regionsAreEqual) {
        /* set from cookie, maybe city name instead region name */
        this.region = regionFromCookie;
        return;
      }

      /* Route without region segment */
      if (!regionFromUrl) {
        /* in region-less route list */
        if (isRouteRegionless) {
          this.region = regionFromCookie;
        } else {
          /* default region, but need set new cookie */
          this.setCookiesServer(this.region);
        }

        return;
      }
    } else {
      /* Detect only if region not set in cookie or URL */
      if (!regionFromUrl) {
        const regionByIp = await this.getRegionByIP();

        if (regionByIp) {
          this.regionByIP = regionByIp;
        }
      }
    }

    this.region = region;
    this.setCookiesServer(this.region);
    this.checkDifferenceRegions();
    return;
  }

  /* todo: refactor */
  @action
  async initClientRegionValue(): Promise<void> {
    //const regionSlugFromQuery = window.location.search?.region as string;
    //await this.checkRegionFromURL(regionSlugFromQuery);
    const regionCookie = getClientCookie(REGION_COOKIE_NAME);
    if (regionCookie) {
      this.region = regionCookie as Entity.Region;
    }
    //this.checkDifferenceRegions();
  }

  // если выбранный регион и регион проживания не совпадают
  // показываем модальное окно выбора региона один раз
  checkDifferenceRegions(): void {
    if (
      this.regionByIP &&
      this.region &&
      this.regionByIP.id !== this.region.id
    ) {
      if (this.needToCheckRegion()) {
        this.setNeedToCheckRegion(true);
      }
    }
  }

  async getRegionByGeoObject(
    geoObject: Entity.GeoObject
  ): Promise<Entity.Region> {
    if (!geoObject) return geoObject;

    const currentCode = geoObject.code || geoObject.id;
    const { data, errors } = await this.ctx.api.methods.settings.region({
      regionID: currentCode,
    });

    if (errors) {
      console.error(errors);
    }

    if (data?.region) {
      return {
        ...data.region,
        name: geoObject.name, // save city name for display
        ...(geoObject?.type === "2" && { cityId: geoObject.id }), // save city id for bitrix cookie
      };
    }

    const dataGeo = _omit(geoObject, ["type", "code"]);

    return {
      ...dataGeo,
      id: geoObject.code || geoObject.id,
    };
  }
}
